import api from './api'

export interface CompletedFormFile {
  file_url: string
  download_url?: string
  file_extension: string
  file_name?: string
  is_previewable: boolean
}

export interface CompletedForm {
  form_id: number
  fk_form_id?: number
  fk_material_id?: number
  user_id?: number
  form_from?: string
  form_subject?: string
  date_sent?: string
  form_content?: string
  type?: string
  status: string
  email_type?: string
  attachments?: string
  date_deleted?: string
  created_at?: string
  updated_at?: string
  material?: { id: number; title: string }
  user?: { id: number; email: string; profile_picture?: string; details?: { first_name: string; last_name: string } }
  file_url?: string
  file_extension?: string
  is_previewable?: boolean
  files?: CompletedFormFile[]
  material_id?: number
}

export const completedFormService = {
  async fetchAll(): Promise<CompletedForm[]> {
    const res = await api.get<{ success: boolean; data: CompletedForm[] }>('/completed-forms')
    return res.data?.data || []
  },

  async create(data: FormData | any): Promise<CompletedForm> {
    const res = await api.post<{ success: boolean; data: CompletedForm }>('/completed-forms', data)
    return res.data?.data || res.data as unknown as CompletedForm
  },

  getDownloadUrl(id: string | number, fileIndex = 0): string {
    const numericId = typeof id === 'string' && id.startsWith('local_')
      ? id.replace('local_', '')
      : String(id)
    const base = (process.env.VUE_APP_API_URL || '/api').replace(/\/$/, '')
    return `${base}/completed-forms/${numericId}/download?file=${fileIndex}`
  },

  async printFile(downloadUrl: string): Promise<void> {
    const token = localStorage.getItem('token')
    const response = await fetch(downloadUrl, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
    if (!response.ok) throw new Error('Failed to fetch file for printing')

    const contentType = response.headers.get('content-type') || ''
    const blob = await response.blob()
    const isPdf = contentType.includes('pdf') || blob.type.includes('pdf')
    const isImage = contentType.startsWith('image/') || blob.type.startsWith('image/')

    if (!isPdf && !isImage) {
      const url = URL.createObjectURL(blob)
      window.open(url, '_blank')
      URL.revokeObjectURL(url)
      throw new Error('This file type cannot be printed directly. The file has been opened in a new tab.')
    }

    const blobUrl = URL.createObjectURL(blob)
    const printWindow = window.open(blobUrl, '_blank')
    if (!printWindow) {
      URL.revokeObjectURL(blobUrl)
      throw new Error('Popup blocked. Please allow popups to print this file.')
    }

    const triggerPrint = () => {
      try {
        printWindow.focus()
        printWindow.print()
      } catch {
        // Browser may block print until the PDF viewer is ready
      }
    }

    printWindow.onload = () => setTimeout(triggerPrint, 1000)
    setTimeout(triggerPrint, 3000)

    setTimeout(() => URL.revokeObjectURL(blobUrl), 60000)
  }
}
